import { useLayoutEffect, useState } from "react";
import type { QueryClient } from "@/lib/query";
import { authKeys } from "./keys";
import type { SessionView } from "./types";

const viewerByQueryClient = new WeakMap<QueryClient, string>();

/** Block authenticated paint until cache ownership matches the current user. */
export function useViewerCacheBoundary(
  session: SessionView | undefined,
  queryClient: QueryClient | null,
): boolean {
  const viewerId = session?.user.id ?? null;
  const [readyViewerId, setReadyViewerId] = useState<string | null>(null);
  useLayoutEffect(() => {
    if (viewerId === null || queryClient === null || session === undefined)
      return;
    reconcileViewerCache(queryClient, viewerId, session);
    setReadyViewerId(viewerId);
  }, [queryClient, session, viewerId]);
  if (queryClient === null) return viewerId !== null;
  return viewerId !== null && readyViewerId === viewerId;
}

function reconcileViewerCache(
  queryClient: QueryClient,
  viewerId: string,
  session: SessionView,
): void {
  const previousViewerId = viewerByQueryClient.get(queryClient);
  if (previousViewerId !== undefined && previousViewerId !== viewerId) {
    queryClient.clear();
    queryClient.setQueryData(authKeys.session(), session);
  }
  viewerByQueryClient.set(queryClient, viewerId);
}
